import { arrayMove } from '@dnd-kit/sortable';
import { Alert, Modal as ConfirmModal } from 'antd';
import type { LabeledValue, RenderSettingsScope } from 'common/models';
import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import { Button, FlexRow } from 'components';
import { getScopeOptions, isDefaultRenderSettings } from 'helpers/renderSettings';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import RenderSettingsList from './RenderSettingsList';

interface Props {
  selectedFields: string[];
  onConfirm: (fieldIds: string[]) => void;
  scope: RenderSettingsScope;
  optionsToHide?: string[];
  enableReset?: boolean;
  handleClose: () => void;
  openInSettings?: boolean;
  // Use for dynamic options, like serivceObjectRevisionCategories
  additionalOptions?: LabeledValue[];
}

const RenderSettingsDrawerContent: React.FC<Props> = (props): React.ReactElement => {
  const {
    selectedFields,
    onConfirm,
    scope,
    optionsToHide,
    handleClose,
    openInSettings,
    additionalOptions
  } = props;

  const { t } = useCustomTranslation();
  const companyAndUserInfoQuery = useGetCompanyAndUserInfoQuery();

  const appUser = companyAndUserInfoQuery.data?.user;

  const [selectedFieldsConfig, setSelectedFieldsConfig] = useState<string[]>(() =>
    selectedFields.filter((x) => !optionsToHide?.includes(x))
  );

  const options = useMemo(() => getScopeOptions(scope), [scope]);

  const visibleOptions = useMemo(
    () => options.filter((x) => !optionsToHide?.includes(x)),
    [options, optionsToHide]
  );

  const filteredOptions = useMemo(
    () => visibleOptions.filter((x) => !selectedFieldsConfig.includes(x)),
    [visibleOptions, selectedFieldsConfig]
  );

  const filteredAdditionalOptions = useMemo(
    () =>
      additionalOptions?.filter(
        (x) => !selectedFieldsConfig.includes(x.value) && !optionsToHide?.includes(x.value)
      ),
    [additionalOptions, optionsToHide, selectedFieldsConfig]
  );

  const handleSortEnd = useCallback((oldIndex: number, newIndex: number) => {
    setSelectedFieldsConfig((prevSelectedFieldsConfig) =>
      arrayMove(prevSelectedFieldsConfig, oldIndex, newIndex)
    );
  }, []);

  const handleNewFieldSelected = useCallback((oldFieldId: string, newFieldId: string) => {
    setSelectedFieldsConfig((prevSelectedFieldsConfig) =>
      prevSelectedFieldsConfig.map((fieldId) => {
        if (oldFieldId === fieldId) {
          return newFieldId;
        }

        return fieldId;
      })
    );
  }, []);

  const handleAddField = useCallback((newFieldId: string) => {
    setSelectedFieldsConfig((prevSelectedFieldsConfig) => [
      ...prevSelectedFieldsConfig,
      newFieldId
    ]);
  }, []);

  const handleFieldRemove = useCallback((fieldId: string) => {
    setSelectedFieldsConfig((prevSelectedFieldsConfig) =>
      prevSelectedFieldsConfig.filter((x) => x !== fieldId)
    );
  }, []);

  // const handleRestoreToDefault = () => setSelectedFieldsConfig(originalFields);

  const handleResetToDefault = useCallback(
    () =>
      ConfirmModal.confirm({
        title: t('renderSettings.resetColumnsToDefault'),
        okText: t('misc.okText'),
        cancelText: t('misc.noText'),
        onOk: () => onConfirm([])
      }),
    [onConfirm, t]
  );

  const handleSave = useCallback(
    () => onConfirm(selectedFieldsConfig),
    [onConfirm, selectedFieldsConfig]
  );

  const enableReset =
    props.enableReset === undefined || openInSettings
      ? !isDefaultRenderSettings(scope, selectedFieldsConfig, optionsToHide)
      : props.enableReset;

  return (
    <div className="RenderSettingsDrawerContent">
      <RenderSettingsList
        selectedFieldsConfig={selectedFieldsConfig}
        options={filteredOptions}
        handleSortEnd={handleSortEnd}
        onFieldIdChange={handleNewFieldSelected}
        onRemoveClick={handleFieldRemove}
        scope={scope}
        enableRemove={selectedFieldsConfig.length > 1}
        handleAddField={handleAddField}
        showAdd={filteredOptions.length > 0 || (filteredAdditionalOptions?.length || 0) > 0}
        filteredAdditionalOptions={filteredAdditionalOptions}
        allAdditionalOptions={additionalOptions}
      />
      <div className="RenderSettingsFooter">
        <FlexRow>
          <div className="d-flex-between">
            <Button onClick={handleClose}>{t('misc.cancelText')}</Button>
            {enableReset && (
              <Button type="link" className="ml-1" onClick={handleResetToDefault}>
                {t('renderSettings.resetButton')}
              </Button>
            )}
          </div>
          <Button type="primary" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </FlexRow>
        {appUser?.isAdmin && !openInSettings && (
          <Alert
            className="RenderSettingsDrawerAlert"
            description={
              <Trans i18nKey="renderSettings.adminColumnAlertText">
                Přizpůsobte tabulku svým potřebám. V případě, že chcete upravit výchozí nastavení
                tabulky pro všechny uživatele, můžete tak učinit v
                <Link to="/settings/configuration"> nastavení firemního prostředí.</Link>
              </Trans>
            }
          />
        )}
      </div>
    </div>
  );
};

export default RenderSettingsDrawerContent;
